import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Users, IndianRupee, Wallet as WalletIcon, TrendingUp, Printer,
  Crown, ArrowDownToLine, ArrowUpFromLine, ClipboardList, Layers,
} from "lucide-react";
import { Link } from "react-router-dom";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const Stat = ({ label, value, icon: Icon, hint, tint = "brand-blue", testid }) => (
  <Card className="p-5 border-border bg-card" data-testid={testid}>
    <div className="flex items-start gap-3">
      <div className={`h-10 w-10 rounded-lg bg-${tint}/10 text-${tint} flex items-center justify-center flex-shrink-0`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="text-2xl font-display font-semibold mt-1 leading-tight">{value}</p>
        {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      </div>
    </div>
  </Card>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [settle, setSettle] = useState(null);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/admin/dashboard"),
      api.get("/admin/wallet"),
      api.get("/admin/settlements"),
    ]);
    setD(a.data);
    setWallet(b.data);
    setSettle(c.data);
  };
  useEffect(() => { load(); }, []);

  if (!d || !wallet || !settle) {
    return <div className="p-6 text-sm text-muted-foreground">Loading admin panel…</div>;
  }

  const r = d.reports;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Super Admin</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Platform Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Live overview of retailers, subscriptions, revenue, commission, wallets and settlements.
        </p>
      </div>

      {/* Top-level metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total retailers" value={d.retailers.total} icon={Users} hint={`${d.retailers.approved} approved · ${d.retailers.pending_approval} pending`} testid="stat-retailers" />
        <Stat label="Active subscriptions" value={d.retailers.active_subscriptions} icon={Crown} hint={`${d.subscription_count} lifetime payments`} tint="brand-green" testid="stat-active-subs" />
        <Stat label="Total orders" value={d.orders_total.toLocaleString("en-IN")} icon={ClipboardList} hint={`${d.prints_total.toLocaleString("en-IN")} pages printed`} tint="fuchsia-500" testid="stat-orders" />
        <Stat label="Total revenue" value={money(d.revenue_total)} icon={IndianRupee} hint="all-time customer payments" tint="brand-green" testid="stat-revenue" />
        <Stat label="Admin commission" value={money(d.commission_total)} icon={TrendingUp} hint="platform earnings on orders" testid="stat-commission" />
        <Stat label="Subscription income" value={money(d.subscription_income)} icon={Layers} hint="paid subscriptions all-time" tint="brand-green" testid="stat-subincome" />
        <Stat label="Pending withdrawals" value={`${d.withdrawals.pending_count} · ${money(d.withdrawals.pending_amount)}`} icon={ArrowUpFromLine} hint={`${d.withdrawals.paid_count} approved (${money(d.withdrawals.paid_amount)})`} tint="amber-500" testid="stat-pending-wd" />
        <Stat label="Pending settlements" value={money(d.pending_settlement)} icon={ArrowDownToLine} hint="owed to retailers (wallet balances)" tint="amber-500" testid="stat-pending-settlement" />
      </div>

      {/* Period reports */}
      <Card className="p-5 border-border bg-card" data-testid="reports-card">
        <h3 className="font-display text-lg font-semibold mb-4">Reports</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "today", label: "Today" },
            { k: "week", label: "Last 7 days" },
            { k: "month", label: "Last 30 days" },
            { k: "lifetime", label: "Lifetime" },
          ].map(({ k, label }) => (
            <div key={k} className="p-4 rounded-lg border border-border bg-secondary/30" data-testid={`report-${k}`}>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
              <p className="mt-2 text-xl font-display font-semibold">{money(r[k].revenue)}</p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                <div><span className="text-foreground font-medium">{r[k].orders}</span> orders</div>
                <div><span className="text-foreground font-medium">{r[k].prints}</span> pages</div>
                <div className="col-span-2"><span className="text-brand-blue font-medium">{money(r[k].commission)}</span> commission</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Admin wallet + Settlement */}
      <Tabs defaultValue="wallet" className="space-y-4">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="wallet" data-testid="tab-wallet">Admin wallet</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="tab-settlements">Settlements</TabsTrigger>
        </TabsList>

        <TabsContent value="wallet" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Commission earned" value={money(wallet.total_commission)} icon={TrendingUp} testid="w-commission" />
            <Stat label="Subscription income" value={money(wallet.subscription_income)} icon={Crown} testid="w-subincome" tint="brand-green" />
            <Stat label="Paid to retailers" value={money(wallet.paid_to_retailers)} icon={ArrowUpFromLine} testid="w-paidout" tint="fuchsia-500" />
            <Stat label="Current balance" value={money(wallet.admin_balance)} icon={WalletIcon} testid="w-balance" tint="brand-blue" />
          </div>
          <Card className="p-5 border-border bg-card" data-testid="wallet-ledger">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-semibold">Wallet ledger</h3>
              <Badge variant="outline">{wallet.ledger.length} entries</Badge>
            </div>
            <div className="rounded-lg border overflow-hidden overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Retailer</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wallet.ledger.slice(0, 100).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-[11px]">{fmtDate(row.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={row.type === "credit" ? "bg-brand-green/10 text-brand-green border-brand-green/20" : "bg-fuchsia-500/10 text-fuchsia-600 border-fuchsia-500/20"}>
                          {row.kind.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{row.description}</TableCell>
                      <TableCell className="text-[11px] font-mono text-muted-foreground">
                        {row.retailer_id ? (
                          <Link to={`/admin/retailers/${row.retailer_id}`} className="hover:underline text-brand-blue">
                            {row.retailer_id.slice(0, 8)}…
                          </Link>
                        ) : "—"}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${row.type === "credit" ? "text-brand-green" : "text-fuchsia-600"}`}>
                        {row.type === "credit" ? "+" : "−"}{money(row.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="settlements" className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Received from retailers" value={money(settle.totals.received)} icon={ArrowDownToLine} tint="brand-green" testid="settle-received" />
            <Stat label="Paid to retailers" value={money(settle.totals.paid)} icon={ArrowUpFromLine} tint="fuchsia-500" testid="settle-paid" />
            <Stat label="Pending settlement" value={money(settle.totals.pending)} icon={WalletIcon} tint="amber-500" testid="settle-pending" />
          </div>
          <Card className="p-5 border-border bg-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-semibold">Per-retailer settlement</h3>
              <Badge variant="outline">{settle.retailers.length} retailers</Badge>
            </div>
            <div className="rounded-lg border overflow-hidden overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Retailer</TableHead>
                    <TableHead>Shop</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {settle.retailers.slice(0, 200).map((r) => (
                    <TableRow key={r.retailer_id} data-testid={`settle-row-${r.retailer_id}`}>
                      <TableCell>
                        <div className="font-medium text-sm">{r.name || "—"}</div>
                        <div className="text-[11px] text-muted-foreground">{r.email}</div>
                      </TableCell>
                      <TableCell className="text-xs">{r.shop_name || "—"}</TableCell>
                      <TableCell className="text-right text-brand-green font-medium">{money(r.received)}</TableCell>
                      <TableCell className="text-right text-fuchsia-600">{money(r.paid)}</TableCell>
                      <TableCell className="text-right font-medium">{money(r.pending)}</TableCell>
                      <TableCell>
                        <Link to={`/admin/retailers/${r.retailer_id}`}>
                          <Button size="sm" variant="outline" data-testid={`settle-view-${r.retailer_id}`}>View</Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
          <Card className="p-5 border-border bg-card" data-testid="settlement-history">
            <h3 className="font-display text-lg font-semibold mb-3">Settlement history · {settle.history.length} events</h3>
            <div className="rounded-lg border overflow-hidden overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Retailer</TableHead>
                    <TableHead>Shop</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {settle.history.slice(0, 200).map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="font-mono text-[11px]">{fmtDate(h.processed_at)}</TableCell>
                      <TableCell className="text-xs">
                        <Link to={`/admin/retailers/${h.retailer_id}`} className="hover:underline text-brand-blue">
                          {h.retailer_name || h.retailer_email || h.retailer_id?.slice(0, 8)}
                        </Link>
                      </TableCell>
                      <TableCell className="text-xs">{h.shop_name || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{h.note || "—"}</TableCell>
                      <TableCell className="text-right font-medium text-fuchsia-600">{money(h.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
